import { Injectable } from '@angular/core';
import { ObservableRequestService } from 'app/services/observable-request.service';
import { ConstantsService } from 'app/services/constants-service';
import { Observable } from 'rxjs/Observable';
import { plainToClass } from 'class-transformer';
import { Organisation } from 'app/shared/models/organisation.model';
import { Configuration } from 'app/shared/models/configuration.model';
import { TenantConfiguration } from 'app/shared/models/tenant-configuration.model';
import { TenantOffice } from 'app/shared/models/tenant-office.model';

@Injectable()
export class TenantConfigurationService {

    constructor(private requestService: ObservableRequestService) { }

    getOffices(): Observable<any> {
        return this.requestService.getRequest(ConstantsService.TENANT_OFFICE)
            .map(response => {
                return plainToClass(TenantOffice, response.arrResult as Object);
            });
    }

    getTenantConfigurations(): Observable<any> {
        return this.requestService.getRequest(ConstantsService.TENANT_CONFIGURATION).map(response => {
            return plainToClass(TenantConfiguration, response.arrResult as Object[]);
        });
    }

    setHeadOffice(id: number): Observable<any> {
        return this.requestService.postRequest(ConstantsService.TENANT_OFFICE_HEAD_OFFICE,
            this.requestService.getFormUrlEncoded({ officeId: id }),
            this.requestService.getFormEncodedContentType());
    }


    updateConfigs(configs: Configuration[]): Observable<any> {
        return this.requestService.postRequest(ConstantsService.TENANT_CONFIGURATION, JSON.stringify(configs),
            this.requestService.getJSONContentType());
    }

    deleteSubOffice(officeId: number): Observable<any> {
        return this.requestService.deleteRequest(ConstantsService.TENANT_OFFICE + "/" + officeId, null, this.requestService.getJSONContentType());
    }

    addSubOffice(officeId: number): Observable<any> {
        return this.requestService.postRequest(ConstantsService.TENANT_OFFICE,
            this.requestService.getFormUrlEncoded({ officeId: officeId }),
            this.requestService.getFormEncodedContentType());
    };

    /**
     * Starts migration of all existing documents from database to S3 (async).
     */
    migrateDocumentsToS3(): Observable<any> {
        return this.requestService.postRequest(ConstantsService.DOCUMENT_MIGRATION_MIGRATE, null, this.requestService.getJSONContentType());
    }

    /**
     * Gets the current status of the migration.
     */
    getMigrationStatus(): Observable<any> {
        return this.requestService.getRequest(ConstantsService.DOCUMENT_MIGRATION_STATUS);
    }

    /**
     * Starts verification of migrated documents (async).
     */
    verifyDocumentMigration(): Observable<any> {
        return this.requestService.postRequest(ConstantsService.DOCUMENT_MIGRATION_VERIFY, null, this.requestService.getJSONContentType());
    }

    /**
     * Gets the current status of the verification.
     */
    getVerificationStatus(): Observable<any> {
        return this.requestService.getRequest(ConstantsService.DOCUMENT_MIGRATION_VERIFY_STATUS);
    }

    /**
     * Tests uploading a single document to S3 without updating the database record.
     * Automatically selects the first unmigrated document.
     */
    testDocumentUpload(): Observable<any> {
        return this.requestService.postRequest(
            ConstantsService.DOCUMENT_MIGRATION_TEST,
            null,
            this.requestService.getJSONContentType()
        );
    }

    /**
     * Registers all person/organisation entities in the tenant that are not yet registered with
     * the external screening service (Beamar). Admin-only.
     */
    registerAllEntitiesForScreening(maxEntities?: number): Observable<any> {
        let url = ConstantsService.TENANT_SCREENING_REGISTER_ALL;
        if (maxEntities != null && maxEntities > 0) {
            url += '?maxEntities=' + encodeURIComponent(String(maxEntities));
        }
        return this.requestService.postRequest(url, null, this.requestService.getJSONContentType());
    }

    previewBankStatementCleanup(beforeDate: string): Observable<any> {
        const url = ConstantsService.BANK_STATEMENT_BATCH_CLEANUP_PREVIEW
            + '?beforeDate=' + encodeURIComponent(beforeDate);
        return this.requestService.getRequest(url);
    }

    cleanupBankStatements(beforeDate: string): Observable<any> {
        const url = ConstantsService.BANK_STATEMENT_BATCH_CLEANUP
            + '?beforeDate=' + encodeURIComponent(beforeDate);
        return this.requestService.postRequest(url, null, this.requestService.getJSONContentType());
    }

    previewScreeningWorkflowCleanup(): Observable<any> {
        return this.requestService.getRequest(ConstantsService.TENANT_SCREENING_CLEANUP_PREVIEW);
    }

    cleanupOpenScreeningWorkflows(): Observable<any> {
        return this.requestService.postRequest(
            ConstantsService.TENANT_SCREENING_CLEANUP,
            null,
            this.requestService.getJSONContentType());
    }

}


// WEBPACK FOOTER //
// ./src/app/admin/tenantconfiguration/tenant-configuration.service.ts